import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  Location,
  PolicyRecommendation,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { failIfValidOrNonExhaustive } from '../../Utils';
import { getCountryName } from '../../Utils';
import { smallMediaQuery } from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import {
  policyColors,
} from '../rockSongChart/Utils';
import {
  ContentContainer,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

export const iconWidth: number = 14;
export const iconMargin: number = 5;

//#region Styling
export const PolicyNameContainer = styled.div`
  margin: 1.5rem 0 1rem;
  display: flex;
  align-items: center;
  position: relative;

  @media ${smallMediaQuery} {
    margin: 1rem 0 0.7rem;
  }
`;
export const PolicyName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
  padding-left: ${iconWidth + iconMargin}px;

  @media ${smallMediaQuery} {
    font-size: 1rem;
  }
`;
export const IconContainer = styled.div`
  width: ${iconWidth}px;
  height: 21px;
  margin-right: ${iconMargin}px;
  position: absolute;
  top: 4px;

  polygon {
    fill: var(--icon-color);
  }

  @media ${smallMediaQuery} {
    width: ${iconWidth * 0.7}px;
    height: 15px;
  }
`;
const PolicyExplanation = styled.div`
  color: #333;
  font-size: 1.2rem;
  line-height: 1.25;
  margin-bottom: 2rem;

  @media ${smallMediaQuery} {
    font-size: 0.9rem;
  }
`;
//#endregion

const query = gql`
  query RockSongChartSideBar($id: ID!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $id) {
      coiClassification
      policyRecommendation
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id']
    shortName: Location['shortName']
    nameAbbr: Location['nameAbbr']
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    coiClassification: CountryProfile['coiClassification'],
    policyRecommendation: CountryProfile['policyRecommendation'],
  };
}

interface Variables {
  id: string;
  year: number;
  productClass: ProductClass;
}

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  countryId: string;
  year: number;
  fluentBundle: FluentBundle;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}
const DiversificationPaths = (props: Props) => {
  const {
    getFluentString, onNextSlideClick, countryId, year,
    fluentBundle, onPrevSlideClick, isHelpActive, helpTooltipIndex,
  } = props;
  const variables: Variables = {
    id: countryId,
    year,
    productClass: ProductClass.SITC,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = null;
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {
    const {location, countryProfile} = data;
    if (location === null || countryProfile === null) {
      out = null;
    } else {
      const {shortName, nameAbbr, thePrefix } = location;
      const {coiClassification, policyRecommendation} = countryProfile;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      if (coiClassification === null || policyRecommendation === null) {
        out = null;
      } else {
        const para1Args = {
          'country-name-first': namePrimary,
          'country-name': nameSecondary,
          'coi-classification': coiClassification,
          'knowhowGlossaryTerm': (
            <GlossaryTerm
              type={TooltipType.Standard}
              term={getFluentString('glossary-knowhow-term-name').toLowerCase()}
              explanation={getFluentString('glossary-knowhow-term-definition')}
              citation={undefined}
              align={TooltipAlign.Standard}
              attachment={null}
            />
          ),
        };
        const policy = policyRecommendation;
        const policyColor = policyColors[policy];
        const policyNameStyle: React.CSSProperties = {
          color: policyColor,
        };
        const iconStyle: React.CSSProperties = {
          '--icon-color': policyColor,
        };

        let policyName: string, policyExplanation: string;
        if (policy === PolicyRecommendation.ParsimoniousIndustrial) {
          policyName = getFluentString('strategic-approach-name-parsimonious-industrial');
          policyExplanation = getFluentString('strategic-approach-explanation-parsimonious-industrial');
        } else if (policy === PolicyRecommendation.StrategicBets) {
          policyName = getFluentString('strategic-approach-name-strategic-bets');
          policyExplanation = getFluentString('strategic-approach-explanation-strategic-bets');

        } else if (policy === PolicyRecommendation.LightTouch) {
          policyName = getFluentString('strategic-approach-name-light-touch');
          policyExplanation = getFluentString('strategic-approach-explanation-light-touch');

        } else if (policy === PolicyRecommendation.TechFrontier) {
          policyName = getFluentString('strategic-approach-name-tech-frontier');
          policyExplanation = getFluentString('strategic-approach-explanation-tech-frontier');
        } else {
          failIfValidOrNonExhaustive(policy, 'Invalid policy ' + policy);
          policyName = '';
          policyExplanation = '';
        }
        out = (
          <>
            <SlideAdvancer
            onClick={onPrevSlideClick}
            direction={Direction.Previous}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
              {getFluentString('section-nav-slide-diversification-countries-paths', {
                'country-name': shortName,
              })}
            </SlideAdvancer>
            <ContentContainer>
            <Title>{getFluentString('section-nav-slide-diversification-strategic-approach')}</Title>
            <Localized
              bundle={fluentBundle}
              messageId='diversification-paths-intro-text-para-1'
              messageArgs={para1Args}
            >
              <IntroText />
            </Localized>
            <PolicyNameContainer>
              <IconContainer
                style={iconStyle}
                dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
              />
              <PolicyName style={policyNameStyle}>{policyName}</PolicyName>
            </PolicyNameContainer>
            <PolicyExplanation>{policyExplanation}</PolicyExplanation>
            </ContentContainer>
            <SlideAdvancer
            onClick={onNextSlideClick}
            direction={Direction.Next}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
              {getFluentString('section-nav-slide-growth-opportunities')}
            </SlideAdvancer>
          </>
        );
      }
    }
  } else {
    out = null;
  }

  return (
    <Slide>
      <>
        {out}
      </>
    </Slide>

  );
};

export default DiversificationPaths;
